import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, shallowRef, watch } from "vue";
import { useStore } from "@/stores";
import { formatApiUrl, isEmptyOrWhiteSpace } from "@/utils/Common";
import { showConfirm, showError, showWarning } from "@/utils/UI";

import {
  API_BIS_IMPORT_PREVIEW,
  API_BIS_IMPORT_MAPPING,
  API_BIS_IMPORT_SUBMIT,
  API_BIS_UPLOAD_PATENT,
} from "@/configs/Apis";
import DuduAjax from "@/utils/DuduAjax";
import { isFunction } from "lodash";

declare interface ImportFieldMapping {
  entityField: string;
  entityFieldLabel: string;
  excelHeader: string;
  primaryKey: boolean;
}

declare interface FieldMapEntity {
  fieldMappingLists: Array<ImportFieldMapping>;
  unMatchedHeaders: Array<string>;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectImportDialog',
  props: { projectId: { type: Number, required: true } },
  emits: ["saved", "close", "step-change", "processing"],
  setup(__props, { expose, emit }) {

const props = __props;





const store = useStore();

const show = ref(false);

const activeStep = ref(0);
const excelFile = ref<Nullable<{ name: string; url: string }>>(null);
const processing = ref(false);
const importTaskId = ref("");
const uploader = shallowRef<any>(null);

const preview = reactive({
  columns: [""] as Array<any>,
  data: [] as Array<any>,
});

const mapping = reactive({
  allExcelHeaders: [] as Array<any>,
  data: [] as Array<ImportFieldMapping>,
});

const duplicateStrategy = ref("SKIP");
const autoCreateEnum = ref(true);
const autoCreateCategory = ref(true);
const stepStatus = reactive({
  uploaded: false,
  previewLoaded: false,
  mappingLoaded: false,
});

const importData = computed<any>(() => (store.state as any).synctask.importTask);

const taskImportStatus = computed(() => {
  let message = "";
  switch (importData.value.status) {
    case "RUNNING":
      message = "导入运行中";
      break;
    case "COMPLETED":
      message = "导入完成";
      break;
    case "NOT_STARTED":
    default:
      message = "未开始";
      break;
  }
  return message;
});

const importPercentage = computed(() => {
  const message = importData.value;
  if (message.status === "COMPLETED") {
    return 100;
  }
  if (message.status === "NOT_STARTED") {
    return 0;
  }
  return parseInt((((message.finished + message.skipped) / (message.count || 1)) * 100).toString());
});

const uploadApi = computed(() => `/api/${formatApiUrl(API_BIS_UPLOAD_PATENT, { dev: 0 })}`);

const isEmpty = (value: string) => isEmptyOrWhiteSpace(value);

const resetImportState = () => {
  activeStep.value = 0;
  excelFile.value = null;
  processing.value = false;
  importTaskId.value = "";
  preview.columns = [""];
  preview.data = [];
  mapping.allExcelHeaders = [];
  mapping.data = [];
  duplicateStrategy.value = "SKIP";
  autoCreateEnum.value = true;
  autoCreateCategory.value = true;
  Object.assign(stepStatus, { uploaded: false, previewLoaded: false, mappingLoaded: false });
};

const open = () => {
  resetImportState();
  show.value = true;
};

const closeImportForm = () => {
  show.value = false;
  emit("close", ((importData.value || {}) as any).status);
  if (((importData.value || {}) as any).status === "COMPLETED") {
    emit("saved");
  }
};

const validateFileType = (file: File) => {
  const ext = /\.\w+$/g.exec(file.name);
  if (!ext || [".xls", ".xlsx"].indexOf(ext[0].toLowerCase()) === -1) {
    showWarning("导入文件格式只能是Excel");
    return false;
  }
  return true;
};

const onUploadChange = (file: File) => {
  if (excelFile.value && excelFile.value.name === file.name) {
    return;
  }
  excelFile.value = {
    name: file.name,
    url: "",
  } as any;
};

const onRemoveFile = () => {
  excelFile.value = null;
  uploader.value.uploadFiles = [];
  stepStatus.uploaded = false;
};

const onUploadSuccess = (res: { location: string }) => {
  processing.value = false;
  stepStatus.uploaded = true;
  if (excelFile.value) {
    excelFile.value.url = res.location.replace(/"/g, "");
  }
  activeStep.value += 1;
};

const onUploadError = (...args: any[]) => {
  showError(args[0]);
  processing.value = false;
};

const doStep0 = () => {
  if (stepStatus.uploaded && !uploader.value.uploadFiles.length) {
    activeStep.value += 1;
    return;
  }
  if (!uploader.value.uploadFiles || !uploader.value.uploadFiles.length) {
    showWarning("请选择上传的文件");
    return;
  }
  processing.value = true;
  uploader.value.submit();
  stepStatus.mappingLoaded = false;
  stepStatus.previewLoaded = false;
};

const onStep1Load = async () => {
  debugger;
  if (stepStatus.previewLoaded) {
    return;
  }
  processing.value = true;
  const result: any = await DuduAjax.post<{ header: Array<string>; data: Array<Array<any>> }>(API_BIS_IMPORT_PREVIEW, {
    fileUrl: excelFile.value?.url,
    projectId: props.projectId,
  }).catch(() => "");
  if (result) {
    const { header, data } = result;
    preview.columns = header;
    preview.data = data.map((item: Array<any>) =>
      item.reduce((prev: any, current: any, index: number) => {
        prev[`col${index}`] = current;
        return prev;
      }, {})
    );
    stepStatus.previewLoaded = true;
  }
  processing.value = false;
};

const onStep2Load = async () => {
  if (stepStatus.mappingLoaded) {
    return;
  }
  processing.value = true;
  const entity: any = await DuduAjax.post<FieldMapEntity>(API_BIS_IMPORT_MAPPING, {
    fileUrl: excelFile.value?.url,
    projectId: props.projectId,
  }).catch(() => "");
  if (entity) {
    mapping.data = entity.fieldMappingLists;
    const excelHeaders: Array<string> = [];
    entity.fieldMappingLists.forEach((field: ImportFieldMapping) => {
      if (!isEmptyOrWhiteSpace(field.excelHeader)) {
        excelHeaders.push(field.excelHeader);
      }
    });
    Array.prototype.push.apply(excelHeaders, entity.unMatchedHeaders);
    mapping.allExcelHeaders = excelHeaders;
    stepStatus.mappingLoaded = true;
  }
  processing.value = false;
};

const doStep1 = () => {
  activeStep.value += 1;
};

const doStep2 = async () => {
  // 检查匹配字段
  const data: Array<ImportFieldMapping> = mapping.data;
  if (!mapping.data || !mapping.data.length) {
    showWarning("没有找到导入字段映射");
    return;
  }
  let hasUnMatched = false;
  for (let i = 0; i < data.length; i++) {
    if (data[i].primaryKey && isEmptyOrWhiteSpace(data[i].excelHeader)) {
      showWarning("主键字段没有匹配，不能导入");
      return;
    }
    if (isEmptyOrWhiteSpace(data[i].excelHeader)) {
      hasUnMatched = true;
      break;
    }
  }

  const confirmed: any = await showConfirm(`${hasUnMatched ? "存在没有匹配的字段，" : ""}是否执行导入？`).catch(
    () => ""
  );
  if (!confirmed) {
    return;
  }
  const postedParams = {
    projectId: props.projectId,
    fileUrl: excelFile.value?.url,
    duplicateStrategy: duplicateStrategy.value,
    autoCreateEnum: autoCreateEnum.value,
    autoCreateCategory: autoCreateCategory.value,
    fieldMappingList: JSON.parse(JSON.stringify(mapping.data)),
  };
  const taskId: string = await DuduAjax.post<string>(API_BIS_IMPORT_SUBMIT, postedParams);
  importTaskId.value = taskId.replace(/"/g, "");
  activeStep.value += 1;
};

const onStep3Load = async () => {
  if (isEmptyOrWhiteSpace(importTaskId.value)) {
    return;
  }
  processing.value = true;
  await store.dispatch("synctask/progressImport", {
    taskId: importTaskId.value,
    projectId: props.projectId,
  });
  processing.value = false;
};

const stepLoadHandlers: { [k: number]: () => void } = {
  1: onStep1Load,
  2: onStep2Load,
  3: onStep3Load,
};

const stepHandlers: { [k: number]: () => void } = {
  0: doStep0,
  1: doStep1,
  2: doStep2,
};

watch(
  () => activeStep.value,
  (step, oldStep) => {
    debugger;
    if (step > oldStep) {
      const handler = stepLoadHandlers[step];
      if (handler && isFunction(handler)) {
        handler();
      }
    }
    emit("step-change", step);
  }
);

watch(processing, () => {
  emit("processing", processing.value);
});

const doNextStep = () => {
  if (processing.value) {
    return;
  }
  const handler = stepHandlers[activeStep.value];
  if (handler && isFunction(handler)) {
    handler();
  }
};

const doPrevStep = () => {
  if (activeStep.value === 0) {
    return;
  }
  activeStep.value -= 1;
};

expose({ open });

return { __sfc: true,props, emit, store, show, activeStep, excelFile, processing, importTaskId, uploader, preview, mapping, duplicateStrategy, autoCreateEnum, autoCreateCategory, stepStatus, importData, taskImportStatus, importPercentage, uploadApi, isEmpty, resetImportState, open, closeImportForm, validateFileType, onUploadChange, onRemoveFile, onUploadSuccess, onUploadError, doStep0, onStep1Load, onStep2Load, doStep1, doStep2, onStep3Load, stepLoadHandlers, stepHandlers, doNextStep, doPrevStep }
}

})